import { createContext, useContext } from "react"

import { VertesiaClient } from "@vertesia/client"
import { ExecutionRunRef } from "@vertesia/common"

import { SharedState, useWatchSharedState } from "@hooks/use-shared-state"

import { addDays } from "@lib/date"
import { decodeBase64Value } from "@lib/decoder"
import { extractSpecificTagFromExecution, extractCollectionIdFromExecution, extractTaskName } from "@lib/workflow"

import { SearchableExecutionRunRef } from "@models/executions"

interface AgentHistoryModel {
    runs: SearchableExecutionRunRef[]
    error?: Error
    loading: boolean
}

export class AgentHistory {
    today: Date
    yesterday: Date
    pastWeek: Date
    result = new SharedState<AgentHistoryModel>({ runs: [], loading: false })

    constructor(
        public client: VertesiaClient,
        public user: string | undefined,
        public limit = 50
    ) {
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

        this.today = today
        this.yesterday = addDays(today, -1)
        this.pastWeek = addDays(today, -7)
    }

    get runs() {
        return this.result.value.runs
    }

    getActiveConversations(runs: SearchableExecutionRunRef[]) {
        return runs.filter((run) => run.status === "to_review" || run.status === "in_progress").sort((a, b) => (b.status ?? "").localeCompare(a.status ?? ""))
    }

    getPreviousConversations(runs: SearchableExecutionRunRef[]) {
        return runs.filter((run) => run.status !== "to_review" && run.status !== "in_progress")
    }

    getTodayRuns(runs: SearchableExecutionRunRef[]) {
        return runs.filter((run) => {
            return new Date(run.updated_at) >= this.today
        })
    }

    getYesterdayRuns(runs: SearchableExecutionRunRef[]) {
        return runs.filter((run) => {
            return new Date(run.updated_at) < this.today && new Date(run.updated_at) >= this.yesterday
        })
    }

    getPastWeekRuns(runs: SearchableExecutionRunRef[]) {
        return runs.filter((run) => {
            return new Date(run.updated_at) < this.today && new Date(run.updated_at) < this.yesterday && new Date(run.updated_at) >= this.pastWeek
        })
    }

    getOlderRuns(runs: SearchableExecutionRunRef[]) {
        return runs.filter((run) => {
            return new Date(run.updated_at) < this.pastWeek
        })
    }

    get error() {
        return this.result.value.error
    }

    get isLoading() {
        return this.result.value.loading
    }

    reset() {
        this.result.value = {
            runs: [],
            loading: false,
        }
    }

    updateRunByPredicate(predicate: (run: SearchableExecutionRunRef) => boolean, updatedRun: SearchableExecutionRunRef): boolean {
        const currentRuns = [...this.result.value.runs]
        const index = currentRuns.findIndex(predicate)

        if (index === -1) {
            return false
        }

        currentRuns[index] = updatedRun

        this.result.value = {
            ...this.result.value,
            runs: currentRuns,
        }

        return true
    }

    updateRunById(runId: string, updatedRun: SearchableExecutionRunRef): boolean {
        return this.updateRunByPredicate((run) => run.run.id === runId, updatedRun)
    }

    async _search() {
        if (this.isLoading) {
            return Promise.resolve(false)
        }

        this.result.value = {
            loading: true,
            runs: [],
        }

        const collections = await this.client.store.collections.search({})

        const currentDate = new Date()
        const start = addDays(currentDate, -31).toISOString()
        const created_by = `user:${this.user}`

        try {
            let searchableRuns: SearchableExecutionRunRef[] = []
            const conversations = await this.client.workflows.listConversations({ start })
            if (conversations && conversations.runs) {
                await Promise.all(
                    conversations.runs.map(async (run) => {
                        const results = await this.client.runs.list({ filters: { tag: `wf-run:${run.run_id}` } })
                        if (results && results[0]) {
                            try {
                                const tmpRun = results[0]
                                const visibility = decodeBase64Value(run.raw?.searchAttributes?.indexedFields?.Visibility.data)
                                const isPrivate = visibility === "private" || visibility.includes("private")

                                let taskName = undefined
                                let status: "to_review" | "reviewed" | "in_progress" | undefined = undefined

                                const collectionId = extractCollectionIdFromExecution(tmpRun)
                                if (collectionId) {
                                    const collection = collections.find((col) => col.id === collectionId) ?? (await this.client.store.collections.get(collectionId))
                                    if (collection) {
                                        taskName = collection.name
                                        status = (collection as any).properties?.status || "in_progress"
                                    }
                                } else {
                                    taskName = extractTaskName(tmpRun as ExecutionRunRef)
                                }

                                if (!taskName) {
                                    return
                                }
                                searchableRuns.push({
                                    access: isPrivate ? "private" : "shared",
                                    updated_at: new Date(tmpRun.updated_at),
                                    collection_id: collectionId,
                                    name: taskName,
                                    run: tmpRun as ExecutionRunRef,
                                    run_id: run.run_id,
                                    running: run.status === "RUNNING",
                                    status,
                                    workflow_id: run.workflow_id,
                                })
                            } catch (err) {
                                console.error("Error retrieving Agent information:", err)
                            }
                        }
                    })
                )
            }

            const chatIdToRun: Record<string, SearchableExecutionRunRef> = {}

            const chatResponses = await this.client.runs.search({
                query: {
                    created_by,
                    start,
                    tags: ["chat"],
                },
            })
            chatResponses.forEach(async (run) => {
                try {
                    const chatId = extractSpecificTagFromExecution(run, "chat")
                    if (!chatId) {
                        return
                    }

                    let name = undefined
                    const collectionId = extractCollectionIdFromExecution(run)
                    if (collectionId) {
                        const collection = collections.find((col) => col.id === collectionId) ?? (await this.client.store.collections.get(collectionId))
                        if (collection) {
                            name = collection.name
                        }
                    } else {
                        name = extractTaskName(run as ExecutionRunRef)
                    }

                    if (!name) {
                        return
                    }

                    const searchableRun: SearchableExecutionRunRef = {
                        run: run as ExecutionRunRef,
                        collection_id: collectionId,
                        access: "private",
                        name,
                        running: true,
                        updated_at: new Date(run.updated_at),
                    }

                    if (!chatIdToRun[chatId] || new Date(run.updated_at) < chatIdToRun[chatId].updated_at) {
                        chatIdToRun[chatId] = searchableRun
                    }
                } catch (err) {
                    console.error("Error retrieving Agent information:", err)
                }
            })

            const serviceResponses = await this.client.runs.search({
                query: {
                    created_by,
                    start,
                    tags: ["service"],
                },
            })
            serviceResponses.forEach(async (run) => {
                try {
                    const serviceId = extractSpecificTagFromExecution(run, "service")
                    if (!serviceId) {
                        return
                    }

                    let name = undefined
                    const collectionId = extractCollectionIdFromExecution(run)
                    if (collectionId) {
                        const collection = collections.find((col) => col.id === collectionId) ?? (await this.client.store.collections.get(collectionId))
                        if (collection) {
                            name = collection.name
                        }
                    } else {
                        name = extractTaskName(run as ExecutionRunRef)
                    }

                    if (!name) {
                        return
                    }

                    const searchableRun: SearchableExecutionRunRef = {
                        run: run as ExecutionRunRef,
                        collection_id: collectionId,
                        access: "private",
                        name,
                        running: true,
                        updated_at: new Date(run.updated_at),
                    }

                    if (!chatIdToRun[serviceId] || new Date(run.updated_at) < chatIdToRun[serviceId].updated_at) {
                        chatIdToRun[serviceId] = searchableRun
                    }
                } catch (err) {
                    console.error("Error retrieving Agent information:", err)
                }
            })

            Object.entries(chatIdToRun).map(async ([chatId, run]) => {
                const collectionId = run.collection_id
                if (collectionId) {
                    const col = collections.find((col) => col.id === collectionId)
                    if (col) {
                        run.name = col.name || run.name
                    }
                }
                return [chatId, run]
            })

            Object.values(chatIdToRun).forEach((run) => searchableRuns.push(run))

            this.result.value = {
                loading: false,
                runs: searchableRuns.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()),
            }

            return true
        } catch (err) {
            this.result.value = {
                error: err instanceof Error ? err : new Error("Unknown error occurred"),
                loading: false,
                runs: this.runs,
            }
            throw err
        }
    }

    search() {
        if (this.isLoading) {
            return Promise.resolve(false)
        }
        return this._search()
    }
}

export const AgentHistoryContext = createContext<AgentHistory | undefined>(undefined)

export function useSearch() {
    return useContext(AgentHistoryContext)!
}

export function useWatchSearchResult() {
    const search = useSearch()
    const result = useWatchSharedState(search.result)
    return { ...result, search }
}
